import { ApolloError } from '@apollo/client';

import { useAcceptSchoolMemberMutation, UserSchoolMembershipFilter } from 'shared/graphql';
import { useSchoolConfiguration, useToast } from 'shared/hooks';
import { recordError } from 'shared/utils/record';

const APPROVING_MEMBERS_MESSAGE_KEY = 'APPROVING_MEMBERS_MESSAGE';

type Options = Parameters<typeof useAcceptSchoolMemberMutation>[0];

/**
 * Custom hook to accept members into a school.
 *
 * This hook provides a function to accept members by their user keys and
 * handles the success and error notifications accordingly.
 *
 * @returns An object containing:
 * - `mutate`: A function to accept members.
 * - `loading`: A boolean indicating if the mutation is in progress.
 *
 * @example
 * const { mutate, loading } = useAcceptMembers();
 *
 * const handleAcceptMembers = async () => {
 *   await mutate({ id: { equals: 'user1' } });
 * };
 */
export function useAcceptMembers(options?: Options) {
  const { showMessage, showSuccess, showError, dismiss } = useToast();
  const { data: school } = useSchoolConfiguration();

  const [mutation, { loading }] = useAcceptSchoolMemberMutation(options);

  const mutate = async (filter: UserSchoolMembershipFilter) => {
    if (!school) {
      return;
    }

    try {
      showMessage('Requesting approval for selected members(s).', {
        id: APPROVING_MEMBERS_MESSAGE_KEY,
      });

      await mutation({
        variables: {
          filter,
          school: { id: school.id },
        },
      });

      showSuccess(
        'Approval request submitted successfully. The process may take some time, and the user(s) will be notified upon completion.',
      );
    } catch (err) {
      recordError(err);

      if (err instanceof ApolloError) {
        const [issue, ...issues] = err.graphQLErrors ?? [];

        let message = issue.message;

        if (issues.length) {
          message += ` (${issues.length} issues more)`;
        }

        showError(message, { reportable: false });
        return;
      }

      if (err instanceof Error) {
        showError(err.message);
      }
    } finally {
      dismiss(APPROVING_MEMBERS_MESSAGE_KEY);
    }
  };

  return {
    mutate,
    loading,
  };
}
